import { useState, useEffect, memo, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import analytics from '@/lib/mixpanel';
import { toast } from 'sonner';
import { useSubscription } from '@/hooks/useSubscription';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '@/components/ui/carousel';
import { OnboardingSlide1 } from './slides/OnboardingSlide1';
import { OnboardingSlide2 } from './slides/OnboardingSlide2';
import { OnboardingSlidePersonal } from './slides/OnboardingSlidePersonal';
import { OnboardingSlideHormonalStatus, type MenstrualStatus } from './slides/OnboardingSlideHormonalStatus';
import { OnboardingSlideCyclePhase } from './slides/OnboardingSlideCyclePhase';

import { OnboardingSlideGoalsMacro } from './slides/OnboardingSlideGoalsMacro';
import { OnboardingSlideBeta } from './slides/OnboardingSlideBeta';
import { OnboardingSlide5 } from './slides/OnboardingSlide5';

export const OnboardingFlow = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isCompleting, setIsCompleting] = useState(false);
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [selectedGender, setSelectedGender] = useState<string | null>(null);
  const [showHormonalSlides, setShowHormonalSlides] = useState(false);
  const [menstrualStatus, setMenstrualStatus] = useState<MenstrualStatus | null>(null);
  const [showCyclePhaseSlide, setShowCyclePhaseSlide] = useState(false);

  // Determine cycle type based on menstrual status
  const cycleType = menstrualStatus === 'irregular_cycle' ? 'irregular' : 'regular';

  // Build slides array based on gender selection and menstrual status
  const baseSlides = [
    { component: OnboardingSlideBeta, title: 'Join Beta' },
    { component: OnboardingSlide1, title: 'Welcome to FaceFable' },
    { component: OnboardingSlide2, title: 'The Science Behind It' },
    { component: OnboardingSlidePersonal, title: 'Personal Info' },
  ];

  // Only show cycle phase slide for regular or irregular cycle users
  const hormonalSlides = showHormonalSlides ? [
    { component: OnboardingSlideHormonalStatus, title: 'Menstrual Cycle' },
    ...(showCyclePhaseSlide ? [{ component: OnboardingSlideCyclePhase, title: 'Cycle Phase' }] : []),
  ] : [];

  const endSlides = [
    { component: OnboardingSlideGoalsMacro, title: 'Your Goals' },
    { component: OnboardingSlide5, title: 'Get Started' },
  ];

  const slides = [...baseSlides, ...hormonalSlides, ...endSlides];

  const handleGenderSelect = (gender: string) => {
    setSelectedGender(gender);
    // Show hormonal slides for women
    if (gender === 'woman') {
      setShowHormonalSlides(true);
    } else {
      setShowHormonalSlides(false);
      setShowCyclePhaseSlide(false);
      setMenstrualStatus(null);
    }
  };

  const handleMenstrualStatusSelect = (status: MenstrualStatus) => {
    setMenstrualStatus(status);
    // Only show cycle phase slide for regular or irregular cycle users
    const shouldShowCyclePhase = status === 'regular_cycle' || status === 'irregular_cycle';
    setShowCyclePhaseSlide(shouldShowCyclePhase);
  };

  const handleSlideChange = (index: number) => {
    setCurrentSlide(index);
    // Scroll to top when changing slides
    window.scrollTo({ top: 0, behavior: 'smooth' });
    analytics.track('Onboarding Slide Viewed', {
      slide_number: index + 1,
      title: slides[index]?.title || 'Unknown',
    });
  };

  const goToNextSlide = () => {
    carouselApi?.scrollNext();
  };

  const completeOnboarding = async () => {
    setIsCompleting(true);
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError) {
        console.error('Auth error:', authError);
        toast.error('Please sign in to continue');
        navigate('/login');
        return;
      }
      
      if (!user) {
        console.error('No user found');
        toast.error('Please sign in to continue');
        navigate('/login');
        return;
      }

      const { error } = await supabase
        .from('profiles')
        .update({ has_completed_onboarding: true })
        .eq('id', user.id);

      if (error) {
        console.error('Profile update error:', error);
        throw error;
      }

      try {
        queryClient.setQueryData(['onboarding-check', user.id], (prev: any) => ({
          ...(prev || {}),
          has_completed_onboarding: true,
        }));
      } catch (e) {
        // Cache update failed, non-critical
      }
      analytics.track('Onboarding Completed');
      
      // Redirect to paywall after main onboarding
      navigate('/paywall');
    } catch (error) {
      console.error('Onboarding completion error:', error);
      toast.error('Failed to complete onboarding. Please try again.');
    } finally {
      setIsCompleting(false);
    }
  };

  const handleSkip = async () => {
    analytics.track('Onboarding Skipped', {
      slide_number: currentSlide + 1,
    });
    await completeOnboarding();
  };


  return (
    <div className="min-h-screen bg-white flex justify-center px-4 pt-6 pb-4 relative overflow-hidden">
      {/* Brand background subtle pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, hsl(var(--primary)) 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} />
      </div>

      <div className="w-full max-w-4xl relative z-10">
        {/* Progress Indicator */}
        <div className="text-center mb-6">
          <div className="text-sm text-muted-foreground mb-2 font-normal">
            Step {currentSlide + 1} of {slides.length}
          </div>
          <div className="flex gap-1.5 max-w-md mx-auto">
            {slides.map((_, index) => (
              <div
                key={index}
                className={`flex-1 h-1.5 rounded-full transition-all duration-300 ${
                  index <= currentSlide ? 'bg-primary' : 'bg-muted/20'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Carousel */}
        <Carousel
          className="w-full"
          opts={{ 
            align: 'center',
            duration: 0,
            watchDrag: true
          }}
          setApi={(api: CarouselApi) => {
            if (!api) return;
            setCarouselApi(api);
            api.on('select', () => {
              handleSlideChange(api.selectedScrollSnap());
            });
          }}
        >
          <CarouselContent>
            {slides.map((slide, index) => {
              const SlideComponent = slide.component;
              
              // Build props based on slide type
              const slideProps: any = {
                onComplete: completeOnboarding,
                isCompleting,
                isLastSlide: index === slides.length - 1,
                onNext: goToNextSlide,
              };

              // Add gender select callback for Personal Info slide
              if (slide.title === 'Personal Info') {
                slideProps.onGenderSelect = handleGenderSelect;
              }

              // Add menstrual status callback for Menstrual Cycle slide
              if (slide.title === 'Menstrual Cycle') {
                slideProps.onMenstrualStatusSelect = handleMenstrualStatusSelect;
              }

              // Add cycle type for Cycle Phase slide
              if (slide.title === 'Cycle Phase') {
                slideProps.cycleType = cycleType;
              }
              
              return (
                <CarouselItem key={`${slide.title}-${index}`}>
                  <SlideComponent {...slideProps} />
                </CarouselItem>
              );
            })}
          </CarouselContent>
        </Carousel>
      </div>
    </div>
  );
};
